import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'

import { TextLikeFormField, type TextLikeFormFieldProps } from 'slab/TextLikeFormField'
import { cn } from 'utils'
import { mergeRefs } from 'utils/ref_utils'

import { Badge } from './Badge'
import { type BadgeValueType } from './types'
import { useBadgeValues } from './useBadgeValues'
import { useDirtyState } from './useDirtyState'
import { useScreenReaderAnnouncements } from './useScreenReaderAnnouncements'

export type BadgeInputActions = {
  announceAddedValue: (value: string | string[]) => void;
  announceRemovedValue: (value: string) => void;
  clearAnnouncements: () => void;
}

export interface BadgeInputProps<T> extends Omit<TextLikeFormFieldProps, 'className' | 'children'>, Omit<React.InputHTMLAttributes<HTMLInputElement>, 'className'> {
  defaultBadgeValues?: string[];
  imperativeHandle?: React.RefObject<BadgeInputActions>;
  /**
   * A callback that is executed when a value is about to be added (i.e. when the
   * user presses `Enter` or `Tab` within a filled input). By default, the input
   * will be cleared after this callback is executed. Return `false` to prevent
   * the default behavior of clearing the input.
   *
   * With uncontrolled badge values, the value will be added automatically after
   * this callback (unless the callback returns `false`). With controlled badge
   * values, it is up to the `onAdd` callback to handle adding the values.
   */
  onAdd?: (value: string) => boolean | void;
  /**
   * A callback that is executed when a value is about to be removed (i.e. when
   * the user uses the backspace or delete keys to remove a badge, or clicks the
   * "x" button on the badge).
   */
  onRemove?: (value: BadgeValueType<T>, index: number) => boolean | void;
  renderBadge?: (value: BadgeValueType<T>, index: number) => React.ReactElement<typeof Badge>;
  badgeValues?: T;
}

export function BadgeInputFunc<T> (
  {
    defaultBadgeValues,
    imperativeHandle,
    name,
    onAdd: onAddProp,
    onRemove: onRemoveProp,
    renderBadge,
    badgeValues: badgeValuesProp,
    ...props
  }: BadgeInputProps<T>,
  forwardedRef: React.ForwardedRef<HTMLInputElement>
) {
  const [selectedBadgeIndex, setSelectedBadgeIndex] = useState<number | null>(null)

  const badgesContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const { badgeValues, onAdd, onRemove } = useBadgeValues({
    defaultProp: defaultBadgeValues ?? [],
    onAdd: onAddProp,
    onRemove: onRemoveProp,
    prop: badgeValuesProp,
  })

  const {
    announcement,
    announceAddedValue,
    announceRemovedValue,
    announceSelectionChange,
    clearAnnouncements,
  } = useScreenReaderAnnouncements({
    container: badgesContainerRef, selectedBadgeIndex
  })

  const isDirty = useDirtyState(badgeValues)
  const placeholder = isDirty && badgeValues.length > 0 ? '' : props.placeholder

  useImperativeHandle(imperativeHandle, () => ({
    announceAddedValue,
    announceRemovedValue,
    clearAnnouncements,
  }), [announceAddedValue, announceRemovedValue, clearAnnouncements])

  function getSelectedBadgeAtIndex (index: number | null) {
    const container = badgesContainerRef.current

    if (index === null) return null

    return (
      container?.querySelectorAll<HTMLDivElement>('[data-badge]')?.[index] || null
    )
  }

  function selectLastBadge () {
    if (badgeValues.length > 0) {
      setSelectedBadgeIndex(badgeValues.length - 1)
      announceSelectionChange()
    }
  }

  function selectPreviousBadge () {
    if (selectedBadgeIndex !== null) {
      setSelectedBadgeIndex(Math.max(selectedBadgeIndex - 1, 0))
      announceSelectionChange()
    }
  }

  function selectNextBadge () {
    if (selectedBadgeIndex !== null) {
      const nextIndex = selectedBadgeIndex + 1

      if (nextIndex < badgeValues.length) {
        setSelectedBadgeIndex(nextIndex)
        announceSelectionChange()
      } else {
        setSelectedBadgeIndex(null)
        announceSelectionChange()
      }
    }
  }

  function handleKeyDown (event: React.KeyboardEvent<HTMLInputElement>) {
    const input = event.currentTarget
    const value = input.value

    function handleAdd () {
      const followDefaultBehavior = onAdd(value)

      if (followDefaultBehavior) {
        // React overrides the setter for `value` so we need to use
        // `Object.getOwnPropertyDescriptor` to get the original setter
        const setValue = Object?.getOwnPropertyDescriptor(
          HTMLInputElement.prototype,
          'value'
        )?.set

        setValue?.call(input, '')
        input.dispatchEvent(new Event('input', { bubbles: true }))

        announceAddedValue(value)
      }
    }

    function handleRemove () {
      const badge = getSelectedBadgeAtIndex(selectedBadgeIndex)

      if (badge) {
        badge.dispatchEvent(new Event('badge-remove'))
      }
    }

    if ((event.key === 'Enter' || event.key === 'Tab') && value.trim() !== '') {
      event.preventDefault()
      handleAdd()
    }

    if ((event.key === 'Backspace')) {
      if (selectedBadgeIndex !== null) {
        event.preventDefault()

        handleRemove()

        if (badgeValues.length === 1) {
          setSelectedBadgeIndex(null)
        } else if (selectedBadgeIndex > 0) {
          setSelectedBadgeIndex(selectedBadgeIndex - 1)
        }
      } else if (input.selectionStart === 0 && input.selectionEnd === 0) {
        event.preventDefault()
        selectLastBadge()
      }
    }

    if (event.key === 'Delete' && selectedBadgeIndex !== null) {
      event.preventDefault()

      handleRemove()

      if (selectedBadgeIndex === badgeValues.length - 1) {
        setSelectedBadgeIndex(null)
      }
    }

    if (event.key === 'ArrowLeft' && input.selectionStart === 0 && input.selectionEnd === 0) {
      event.preventDefault()

      if (selectedBadgeIndex !== null) {
        selectPreviousBadge()
      } else {
        selectLastBadge()
      }
    }

    if (event.key === 'ArrowRight' && selectedBadgeIndex !== null) {
      event.preventDefault()

      selectNextBadge()
    }

    props.onKeyDown?.(event)
  }

  function renderDefaultBadge (badgeValue: BadgeValueType<T>, index: number): React.ReactElement<typeof Badge> {
    return (
      <Badge key={index}>
        {String(badgeValue)}
      </Badge>
    )
  }

  function renderBadges () {
    const badgesToRender = badgeValues.map((badgeValue, index) => {
      const renderedBadge = renderBadge?.(badgeValue, index) ?? renderDefaultBadge(badgeValue, index)

      if (React.isValidElement(renderedBadge)) {
        return React.cloneElement(renderedBadge, {
          disabled: props.disabled,
          name,
          onRemove: () => {
            const followDefaultBehavior = onRemove(badgeValue, index)

            if (followDefaultBehavior) {
              const text = getSelectedBadgeAtIndex(index)?.textContent || ''

              announceRemovedValue(text)
            }
          },
          selected: selectedBadgeIndex === index,
        } as any)
      }

      return renderedBadge
    })

    if (badgesToRender.length > 0) {
      return (
        <>
          <span className='sr-only'>{badgeValues.length} selected values:</span>
          {badgesToRender}
        </>
      )
    }
  }

  return (
    <TextLikeFormField {...props}>
      {({ className, ...formFieldProps }) => (
        <div
          className='flex items-center flex-wrap gap-2 p-2'
          onClick={() => {
            inputRef.current?.focus()
          }}
          ref={badgesContainerRef}
        >
          {renderBadges()}
          <div
              // The `after` pseudo-element is used to autosize the input to
              // ensure it never shrinks below the width of the placeholder text.
            className='w-auto grow p-0 self-center after:content-[attr(data-autosize)] after:invisible after:block after:h-0 after:whitespace-pre-wrap after:body after:w-auto'
            data-autosize={props.placeholder}
          >
            <input
              {...formFieldProps}
              className={cn(className, 'w-full p-0 body')}
              onBlur={(event) => {
                setSelectedBadgeIndex(null)
                clearAnnouncements()
                props.onBlur?.(event)
              }}
              onChange={(event) => {
                setSelectedBadgeIndex(null)
                clearAnnouncements()
                props.onChange?.(event)
              }}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              ref={mergeRefs(inputRef, forwardedRef)}
              size={1}
              type='text'
            />
          </div>
          <div className='sr-only' aria-live='assertive' role='alert'>
            {announcement}
          </div>
        </div>
      )}
    </TextLikeFormField>
  )
}

const BadgeInputComponent = forwardRef(BadgeInputFunc) as <T>(
  props: BadgeInputProps<T>, ref?: React.Ref<HTMLInputElement>
) => React.ReactElement

export const BadgeInput = Object.assign(BadgeInputComponent, { Badge })
